import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message
} from 'antd'
import { AxiosError } from 'axios'
import React from 'react'
import { createPortal } from 'react-dom'
import InputMask from 'react-input-mask'
import { useNavigate } from 'react-router-dom'
import { MenuInfo } from 'rc-menu/lib/interface'
import {
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  LoginOutlined,
  PaperClipOutlined,
  RightOutlined,
  SaveOutlined,
  SearchOutlined,
  SlidersTwoTone,
  UploadOutlined
} from '@ant-design/icons'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { PhoneVerificationModal } from 'components/ui/PhoneVerificationModal/PhoneVerificationModal'
import { EmailVerificationModal } from 'components/ui/EmailVerificationModal/EmailVerificationModal'
import { ContactVerificationBadge } from 'components/ui/ContactVerificationBadge/ContactVerificationBadge'
import { SellerDisableStatus } from 'components/sellers/SellerDisableStatus/SellerDisableStatus'
import { passwordPattern, ruNamePattern } from 'shared/utils/patterns'
import { normalizeNumber } from 'shared/utils/normalizeNumber'
import {
  ESellerLegalTypes,
  ESellerStatus,
  ESellerTypes,
  ISaveSellerHiddenCategoriesRequest,
  ISeller,
  ISellerFormData,
  ISellerHiddenCategoryRecord,
  TSellerDisableInfo
} from 'models/seller'
import {
  ESellerDisableReason,
  ESellerManualDisableBy,
  TVariantSeller
} from 'models/seller'
import { ERoles } from 'models/auth'
import { sellerApi } from 'api/seller'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import { productApi } from 'api/product'
import { authApi } from 'api/auth'
import { appSelectors } from 'store/app'
import { superAdminRoutes } from 'shared/constants/routes'
import { showServerError } from 'shared/utils/showServerError'
import { IMAGES_URL } from 'shared/constants/urls'
import { showMessage } from 'shared/utils/showMessage'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { getSellerStatusItems, sellerStatuses } from 'shared/constants/sellers'
import { TAX_OPTIONS } from 'shared/constants/product'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useSellerAccess } from 'shared/hooks/useSellerAccess'
import { useStoreHiddenSync } from 'shared/hooks/useStoreHiddenSync'
import { IParsedCategory } from 'models/product'
import { TEmployeeAccess, TEmployeeAccessSection } from 'models/sellerEmployees'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { normalizePhone } from 'shared/utils/normalizePhone'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { SectionTitle } from 'components/ui'
import styles from './SellerInfo.module.scss'
import {
  getLegalSubmitData,
  getEditSubmitData,
  getEmployeeSubmitData
} from './utils/getSubmitData'
import { EmployeeSectionAccess } from './EmployeeSectionAccess/EmployeeSectionAccess'
import type { TextAreaRef } from 'antd/es/input/TextArea'

interface ISellerInfoEditProps {
  mode: 'edit'
  type: TVariantSeller
  sellerId: string
}

interface ISellerInfoCreateProps {
  mode: 'create'
  type: TVariantSeller
}

interface ICompanyInfo {
  companyName: string
  OKPO?: string
  legalKPP: string
  OGRN: string
  organizationType: string
  legalAddress: string
}

// Причины, которые переключатель «Скрыть товары» не снимает: бэкенд
// отклонит сохранение, поэтому выключить его не даём сразу
const getSwitchOffBlockers = (info: TSellerDisableInfo, isAdmin: boolean) => {
  const reasons = info.disabled_reasons ?? []
  const blockers: string[] = []
  // Скрытие без автора — старое, его ставили до записи автора: считаем админским
  if (
    !isAdmin &&
    reasons.includes(ESellerDisableReason.MANUAL) &&
    info.manual_disable?.by !== ESellerManualDisableBy.SELLER
  ) {
    blockers.push(
      'Товары скрыты администратором площадки. Чтобы показать их снова, обратитесь в поддержку.'
    )
  }
  if (reasons.includes(ESellerDisableReason.STOCK_OUTDATED)) {
    blockers.push(
      isAdmin
        ? 'Магазин отключён за просроченные остатки — переключатель это не снимает, даже у администратора. Магазин включится сам, когда продавец обновит или подтвердит остатки.'
        : 'Магазин отключён за просроченные остатки — переключатель это не снимает. Обновите или подтвердите остатки, и магазин включится сам.'
    )
  }
  const until = info.disabled_for_cancels_until
  if (
    !isAdmin &&
    reasons.includes(ESellerDisableReason.CANCELS) &&
    (!until || dayjs(until).isAfter(dayjs()))
  ) {
    blockers.push(
      until
        ? `Магазин отключён за отмены заказов до ${dayjs(until).format('DD.MM.YYYY HH:mm')} и включится сам. Раньше срока включить его может только администратор площадки — обратитесь в поддержку.`
        : 'Магазин отключён за отмены заказов. Включить его может только администратор площадки — обратитесь в поддержку.'
    )
  }
  if (reasons.includes(ESellerDisableReason.PROFILE_INCOMPLETE)) {
    blockers.push(
      isAdmin
        ? 'Профиль не заполнен: у продавца нет рабочего способа доставки — переключатель это не снимает. Магазин включится сам, когда продавец настроит доставку.'
        : 'Профиль не заполнен: нет рабочего способа доставки — переключатель это не снимает. Настройте доставку, и магазин включится сам.'
    )
  }
  return blockers
}

interface IHiddenSubCategoryNode {
  id: string
  title: string
}

interface IHiddenCategoryNode {
  id: string
  title: string
  children: IHiddenSubCategoryNode[]
}

type THiddenCategoriesFilter = 'none' | 'all' | 'hideAll' | 'hidden'

type ISellerInfoProps = ISellerInfoEditProps | ISellerInfoCreateProps

const EMPLOYEE_SECTIONS: TEmployeeAccessSection[] = ['BANKING', 'SHOP']

const EMPLOYEE_SECTION_FIELDS: Record<TEmployeeAccessSection, string[]> = {
  BANKING: ['bankName', 'bankBIK', 'bankPC', 'bankKC', 'cardNumber'],
  SHOP: ['shopName', 'defaultTax', 'logo', 'banner', 'desc']
}

const EMPLOYEE_NAME_HINT = 'Меняет владелец магазина в разделе «Сотрудники»'

const HIDDEN_COMMENT_MAX_LENGTH = 200

const toUploadedImage = (key?: string | null) =>
  key ? [{ uid: key, thumbUrl: `${IMAGES_URL}${key}` }] : []

export const SellerInfo: React.FC<ISellerInfoProps> = (props) => {
  const navigate = useNavigate()

  const token = useAppSelector(appSelectors.selectToken)

  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = React.useState(
    props.mode === 'create' ? false : true
  )
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isHolidaysSaving, setIsHolidaysSaving] = React.useState(false)
  const [isError, setIsError] = React.useState(false)

  // Акция «сниженная комиссия» (управляется супер-админом, отдельный эндпоинт)
  const [currentCommission, setCurrentCommission] = React.useState(0)
  const [commissionDiscountActive, setCommissionDiscountActive] =
    React.useState(false)
  const [commissionDiscountRate, setCommissionDiscountRate] = React.useState('')
  const [commissionDiscountEndDate, setCommissionDiscountEndDate] =
    React.useState<dayjs.Dayjs | null>(null)

  const [deletedPhotos, setDeletedPhotos] = React.useState<string[]>([])

  const [sellerStatus, setSellerStatus] = React.useState<ESellerStatus | null>(
    null
  )
  const [disableInfo, setDisableInfo] =
    React.useState<TSellerDisableInfo | null>(null)
  // null — модалка причины скрытия закрыта
  const [hiddenCommentDraft, setHiddenCommentDraft] = React.useState<
    string | null
  >(null)
  const hiddenCommentRef = React.useRef<TextAreaRef>(null)
  const [isHiddenSaving, setIsHiddenSaving] = React.useState(false)

  const [companyInfo, setCompanyInfo] = React.useState<ICompanyInfo | null>(
    null
  )

  // Подтверждение контактов: сохранённые значения и флаги с сервера, чтобы
  // отличать подтверждённый контакт от только что введённого в форме
  const [savedPhone, setSavedPhone] = React.useState(0)
  const [isPhoneVerified, setIsPhoneVerified] = React.useState(false)
  const [isSendingPhoneCode, setIsSendingPhoneCode] = React.useState(false)
  const [isPhoneModalOpen, setIsPhoneModalOpen] = React.useState(false)
  const [savedEmail, setSavedEmail] = React.useState('')
  const [isEmailVerified, setIsEmailVerified] = React.useState(false)
  const [isSendingEmailCode, setIsSendingEmailCode] = React.useState(false)
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false)

  const [isLegalINNChecking, setIsLegalINNChecking] = React.useState(false)
  const [profileAddressLocks, setProfileAddressLocks] = React.useState({
    legalAddress: false,
    registrationAddress: false,
    actualAddress: false
  })
  const [isHiddenCategoriesOpen, setIsHiddenCategoriesOpen] =
    React.useState(false)
  const [isHiddenCategoriesLoading, setIsHiddenCategoriesLoading] =
    React.useState(false)
  const [isHiddenCategoriesSaving, setIsHiddenCategoriesSaving] =
    React.useState(false)
  const [hiddenCategoriesFilter, setHiddenCategoriesFilter] =
    React.useState<THiddenCategoriesFilter>('none')
  const [hiddenCategoriesSearch, setHiddenCategoriesSearch] = React.useState('')
  const [hiddenCategoryNodes, setHiddenCategoryNodes] = React.useState<
    IHiddenCategoryNode[]
  >([])
  const [expandedCategories, setExpandedCategories] = React.useState<string[]>(
    []
  )
  const [hiddenCategoryIds, setHiddenCategoryIds] = React.useState<string[]>([])
  const [hiddenSubCategoryIds, setHiddenSubCategoryIds] = React.useState<
    string[]
  >([])

  const sellerType: ESellerTypes | undefined = Form.useWatch('type', { form })
  const middleName: string | undefined = Form.useWatch('middleName', { form })
  const noMiddleName: boolean | undefined = Form.useWatch('noMiddleName', {
    form
  })
  const isMiddleNameLength: boolean = Boolean(middleName?.length ?? false)
  const isPassportPhoto: boolean =
    Form.useWatch('passportPhoto', { form, preserve: true })?.length || false
  const phone: string | undefined = Form.useWatch('phone', { form })
  const isAdmin = useAppSelector(appSelectors.selectRole) === ERoles.ADMIN
  const syncStoreHidden = useStoreHiddenSync()
  const email: string | undefined = Form.useWatch('email', { form })

  const currentPhone = normalizePhone(phone ?? '')
  const currentEmail = (email ?? '').trim().toLowerCase()
  const isPhoneConfirmed = isPhoneVerified && currentPhone === savedPhone
  const isEmailConfirmed = isEmailVerified && currentEmail === savedEmail

  const isLegalType = React.useMemo(
    () => sellerType === ESellerTypes.LEGAL,
    [sellerType]
  )

  const isCreate = React.useMemo(() => props.mode === 'create', [props])
  const editSellerId = React.useMemo(
    () => (props.mode === 'edit' ? props.sellerId : ''),
    [props]
  )

  const isProfile = React.useMemo(() => props.type === 'profile', [props])
  const { isEmployee, employee } = useSellerAccess()
  const isEmployeeMode = isProfile && isEmployee

  // Переключатель сохраняется сразу, в обход проверки формы: скрыть товары
  // можно и с незаполненным профилем
  const saveHidden = async (isHidden: boolean, comment?: string) => {
    try {
      setIsHiddenSaving(true)
      const { data } = await sellerApi.updateSellerHidden(editSellerId, {
        is_hidden: isHidden,
        hidden_comment: comment
      })
      setDisableInfo(data)
      setHiddenCommentDraft(null)
      message.success(isHidden ? 'Товары скрыты' : 'Скрытие товаров снято')
      if (isProfile) {
        sellerApi
          .getStockConfirmationStatus()
          .then(({ data: status }) => syncStoreHidden(status))
          .catch(() => undefined)
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsHiddenSaving(false)
    }
  }

  const onHiddenChange = (checked: boolean) => {
    const blockers = !checked && disableInfo ? getSwitchOffBlockers(disableInfo, isAdmin) : []
    if (blockers.length) {
      Modal.warning({
        title: 'Переключатель нельзя выключить',
        content: blockers.map((text) => <p key={text}>{text}</p>)
      })
      return
    }
    if (checked) {
      setHiddenCommentDraft('')
      return
    }
    saveHidden(false)
  }
  // Сотрудник правит «Банковские сведения» и «Данные о магазине», только пока раздел
  // открыт кодом владельца; значение — время, до которого он открыт
  const [employeeAccess, setEmployeeAccess] = React.useState<TEmployeeAccess>({
    BANKING: null,
    SHOP: null
  })
  const isBankingLocked = isEmployeeMode && !employeeAccess.BANKING
  const isShopLocked = isEmployeeMode && !employeeAccess.SHOP
  const getBankingPlaceholder = (placeholder: string) =>
    isBankingLocked ? 'Скрыто' : placeholder
  const [savePortalTarget, setSavePortalTarget] = React.useState<HTMLElement | null>(null)
  React.useLayoutEffect(() => {
    setSavePortalTarget(document.getElementById('seller-save-button-portal'))
  }, [])
  const isAddressDisabled = React.useCallback(
    (field: keyof typeof profileAddressLocks) =>
      isEmployeeMode || (isProfile && profileAddressLocks[field]),
    [isProfile, isEmployeeMode, profileAddressLocks]
  )
  const normalizeNodeId = React.useCallback((value: unknown): string | null => {
    if (!value) {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'object' && value !== null) {
      if ('_id' in value) {
        return String((value as { _id: unknown })._id)
      }
      if ('id' in value) {
        return String((value as { id: unknown }).id)
      }
    }
    return null
  }, [])

  const toHiddenCategoryNodes = React.useCallback(
    (items: IParsedCategory[]) => {
      return (items as unknown[]).reduce<IHiddenCategoryNode[]>(
        (acc, category) => {
          const categoryId = normalizeNodeId(
            (category as { value?: unknown; _id?: unknown; key?: unknown })
              .value ??
              (category as { _id?: unknown })._id ??
              (category as { key?: unknown }).key
          )
          const categoryTitle = String(
            (category as { title?: unknown; name?: unknown }).title ??
              (category as { name?: unknown }).name ??
              ''
          )

          if (!categoryId || !categoryTitle) {
            return acc
          }

          const childrenRaw =
            (category as { children?: unknown[] }).children ?? []
          const children = childrenRaw.reduce<IHiddenSubCategoryNode[]>(
            (subAcc, subCategory) => {
              const subCategoryId = normalizeNodeId(
                (
                  subCategory as {
                    value?: unknown
                    _id?: unknown
                    key?: unknown
                  }
                ).value ??
                  (subCategory as { _id?: unknown })._id ??
                  (subCategory as { key?: unknown }).key
              )
              const subCategoryTitle = String(
                (subCategory as { title?: unknown; name?: unknown }).title ??
                  (subCategory as { name?: unknown }).name ??
                  ''
              )
              if (!subCategoryId || !subCategoryTitle) {
                return subAcc
              }
              subAcc.push({
                id: subCategoryId,
                title: subCategoryTitle
              })
              return subAcc
            },
            []
          )

          acc.push({
            id: categoryId,
            title: categoryTitle,
            children
          })
          return acc
        },
        []
      )
    },
    [normalizeNodeId]
  )

  const isCategoryHidden = React.useCallback(
    (categoryId: string) => hiddenCategoryIds.includes(categoryId),
    [hiddenCategoryIds]
  )

  const isSubCategoryHidden = React.useCallback(
    (subCategoryId: string) => hiddenSubCategoryIds.includes(subCategoryId),
    [hiddenSubCategoryIds]
  )

  const filteredCategoryNodes = React.useMemo(() => {
    const search = hiddenCategoriesSearch.trim().toLowerCase()
    return hiddenCategoryNodes.reduce<IHiddenCategoryNode[]>(
      (acc, category) => {
        const children = category.children.filter((subCategory) => {
          const matchesSearch =
            !search || subCategory.title.toLowerCase().includes(search)
          const isHidden =
            hiddenCategoriesFilter === 'hidden'
              ? isSubCategoryHidden(subCategory.id)
              : true
          return matchesSearch && isHidden
        })

        const categoryMatchesSearch =
          !search || category.title.toLowerCase().includes(search)
        const categoryHidden = isCategoryHidden(category.id)
        const showCategoryByFilter =
          hiddenCategoriesFilter === 'hidden'
            ? categoryHidden || children.length > 0
            : true

        if (!showCategoryByFilter) {
          return acc
        }

        if (!categoryMatchesSearch && !children.length) {
          return acc
        }

        acc.push({
          ...category,
          children:
            categoryMatchesSearch &&
            !search &&
            hiddenCategoriesFilter !== 'hidden'
              ? category.children
              : children
        })
        return acc
      },
      []
    )
  }, [
    hiddenCategoriesFilter,
    hiddenCategoriesSearch,
    hiddenCategoryNodes,
    isCategoryHidden,
    isSubCategoryHidden
  ])

  React.useEffect(() => {
    if (sellerType) {
      form.setFields([
        {
          name: 'bankName',
          errors: []
        },
        {
          name: 'bankBIK',
          errors: []
        },
        {
          name: 'bankPC',
          errors: []
        },
        {
          name: 'bankKC',
          errors: []
        }
      ])
    }
  }, [sellerType])

  React.useEffect(() => {
    if (noMiddleName) {
      form.setFields([
        {
          name: 'middleName',
          errors: [],
          value: undefined
        }
      ])
    }
  }, [noMiddleName])

  React.useEffect(() => {
    if (isMiddleNameLength) {
      form.setFieldValue('noMiddleName', false)
      form.setFields([
        {
          name: 'middleName',
          errors: []
        }
      ])
    }
  }, [isMiddleNameLength])

  const checkLegalINN = async () => {
    try {
      setIsLegalINNChecking(true)
      const response = await sellerApi.checkLegalINN({
        INN: form.getFieldValue('legalINN')
      })
      setCompanyInfo({
        companyName: response.data.value,
        OKPO: response.data.data.okpo,
        legalKPP: response.data.data.kpp,
        OGRN: response.data.data.ogrn,
        organizationType:
          response.data.data.type === ESellerLegalTypes.INDIVIDUAL
            ? 'Индивидуальный предприниматель'
            : 'Юридическое лицо',
        legalAddress: response.data.data.address.value
      })
    } catch {
      form.setFields([
        {
          name: 'legalINN',
          errors: ['Организация не действительна']
        }
      ])
      setCompanyInfo(null)
    } finally {
      setIsLegalINNChecking(false)
    }
  }

  const confirmPhone = async () => {
    if (!/^79\d{9}$/.test(String(currentPhone))) {
      showMessage(
        'error',
        'Некорректный номер телефона',
        'Введите мобильный номер в формате +7 (9XX) XXX-XX-XX'
      )
      return
    }

    try {
      setIsSendingPhoneCode(true)
      // код живёт 10 минут, а отправлять его можно раз в минуту: внутри паузы
      // сразу открываем ввод, иначе продавец не сможет ввести отправленный код
      if (getContactCodeCooldownSeconds(currentPhone) === 0) {
        const { data } = await authApi.sendPhoneCode({ phone: currentPhone })
        if (data.is_phone_verified) {
          setSavedPhone(currentPhone)
          setIsPhoneVerified(true)
          showMessage('success', 'Телефон уже подтверждён', '')
          return
        }
        setContactCodeCooldown(currentPhone)
      }
      setIsPhoneModalOpen(true)
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      if (message?.includes('секунд')) {
        setContactCodeCooldown(currentPhone)
        setIsPhoneModalOpen(true)
      }
      showServerError(e)
    } finally {
      setIsSendingPhoneCode(false)
    }
  }

  const onPhoneVerified = (verifiedPhone: number) => {
    setSavedPhone(verifiedPhone)
    setIsPhoneVerified(true)
  }

  const confirmEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(currentEmail)) {
      showMessage('error', 'Некорректная почта', 'Проверьте почтовый адрес')
      return
    }

    try {
      setIsSendingEmailCode(true)
      if (getContactCodeCooldownSeconds(currentEmail) === 0) {
        await authApi.requestEmailAttachCode({ email: currentEmail })
        setContactCodeCooldown(currentEmail)
      }
      setIsEmailModalOpen(true)
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      if (message?.includes('секунд')) {
        setContactCodeCooldown(currentEmail)
        setIsEmailModalOpen(true)
      }
      showServerError(e)
    } finally {
      setIsSendingEmailCode(false)
    }
  }

  const onEmailVerified = (verifiedEmail: string) => {
    setSavedEmail(verifiedEmail)
    setIsEmailVerified(true)
  }

  React.useEffect(() => {
    if (props.mode === 'edit') {
      const getSeller = async () => {
        try {
          message.loading('Загрузка данных о продавце')
          const { data } = await sellerApi.getSeller(props.sellerId)
          const getFirstFilledAddress = (
            ...values: Array<string | undefined>
          ) =>
            values.find((value) => typeof value === 'string' && value.trim()) ??
            ''
          const isLegalSeller = data.type === ESellerTypes.LEGAL

          const legalAddress = data?.banking_details?.legal_address ?? ''
          const registrationAddress = isLegalSeller
            ? ''
            : data?.additional_info?.registration_address ?? ''
          const actualAddress = isLegalSeller
            ? data?.banking_details?.actual_address ?? ''
            : getFirstFilledAddress(
              data?.additional_info?.actual_address,
              data?.banking_details?.actual_address
            )

          setProfileAddressLocks({
            legalAddress: Boolean(legalAddress?.trim()),
            registrationAddress: Boolean(registrationAddress?.trim()),
            actualAddress: Boolean(actualAddress?.trim())
          })

          setSellerStatus(data.status)
          setDisableInfo(data)
          if (data.employee_access) setEmployeeAccess(data.employee_access)
          setSavedPhone(data.user_id.tel)
          setIsPhoneVerified(Boolean(data.user_id.is_phone_verified))
          setSavedEmail((data.user_id.email ?? '').trim().toLowerCase())
          setIsEmailVerified(Boolean(data.user_id.is_activated))
          setCurrentCommission(data.commission)
          setCommissionDiscountActive(
            Boolean(data.commission_discount?.is_active)
          )
          setCommissionDiscountRate(
            data.commission_discount?.rate != null
              ? String(data.commission_discount.rate)
              : ''
          )
          setCommissionDiscountEndDate(
            data.commission_discount?.end_date
              ? dayjs(data.commission_discount.end_date)
              : null
          )
          form.setFields([
            { name: 'type', value: data.type },
            { name: 'firstName', value: data.user_id.name },
            { name: 'lastName', value: data.user_id.surname },
            { name: 'middleName', value: data.user_id.patronymic },
            { name: 'noMiddleName', value: !data.user_id.patronymic },
            { name: 'email', value: data.user_id.email },
            { name: 'phone', value: phoneMaskStatic(data.user_id.tel) },
            {
              name: 'passportData',
              value: data?.additional_info?.passport_details
            },
            { name: 'companyName', value: data?.company_info?.name },
            { name: 'OKPO', value: data?.company_info?.okpo },
            { name: 'OGRN', value: data?.company_info?.ogrn },
            { name: 'organizationType', value: data?.company_info?.type },
            { name: 'legalAddress', value: legalAddress },
            {
              name: 'registrationAddress',
              value: registrationAddress
            },
            {
              name: 'actualAddress',
              value: actualAddress
            },
            {
              name: 'edoOperator',
              value:
                data?.additional_info?.edo_operator ??
                data?.banking_details?.edo_operator
            },
            {
              name: 'edoId',
              value:
                data?.additional_info?.edo_id ?? data?.banking_details?.edo_id
            },
            { name: 'bankName', value: data.banking_details.name },
            { name: 'bankBIK', value: data.banking_details.BIK },
            { name: 'bankPC', value: data.banking_details.PC },
            { name: 'bankKC', value: data.banking_details.KC },
            { name: 'cardNumber', value: data?.additional_info?.card_number },
            { name: 'shopName', value: data.shop_details.shop_name },
            { name: 'defaultTax', value: data.default_tax ?? undefined },
            { name: 'desc', value: data.shop_details.desc },
            { name: 'holidaysIsActive', value: data.holidaysIsActive }
          ])
          if (data.user_id.birthday) {
            form.setFieldValue('birthDate', dayjs(data.user_id.birthday))
          }
          if (data.type === ESellerTypes.LEGAL) {
            form.setFields([
              { name: 'legalINN', value: data.banking_details.INN },
              { name: 'legalKPP', value: data.banking_details.KPP }
            ])
          }
          if (data.type === ESellerTypes.PHYSICAL) {
            form.setFields([
              { name: 'physicalINN', value: data.banking_details.INN },
              { name: 'physicalKPP', value: data.banking_details.KPP }
            ])
          }
          if (data.shop_details.logo) {
            form.setFieldValue('logo', [
              {
                uid: data.shop_details.logo,
                thumbUrl: `${IMAGES_URL}${data.shop_details.logo}`
              }
            ])
          }
          if (data.shop_details.banner) {
            form.setFieldValue('banner', [
              {
                uid: data.shop_details.banner,
                thumbUrl: `${IMAGES_URL}${data.shop_details.banner}`
              }
            ])
          }
          if (data?.additional_info?.passport_photo) {
            form.setFieldValue('passportPhoto', [
              {
                uid: data.additional_info.passport_photo,
                thumbUrl: `${IMAGES_URL}${data.additional_info.passport_photo}`
              }
            ])
          }
        } catch (e) {
          const error = e as AxiosError
          if (
            (error.response?.status === 500 ||
              error.response?.status === 400 ||
              error.response?.status === 404) &&
            props.type === 'seller'
          ) {
            return navigate(superAdminRoutes.SELLERS)
          }
          showServerError(e)
          setIsError(true)
        } finally {
          message.destroy()
          setIsLoading(false)
        }
      }
      getSeller()
    }
  }, [])

  const logo =
    Form.useWatch('logo', {
      form,
      preserve: true
    }) ?? []

  const banner =
    Form.useWatch('banner', {
      form,
      preserve: true
    }) ?? []

  const setProductsAction = async () => {
    try {
      await sellerApi.setActions({
        toDelete: props.mode === 'edit' ? props.sellerId : ''
      })
      message.success('Продавец успешно удален')
      navigate(superAdminRoutes.SELLERS)
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const showDeleteConfirm = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Вы действительно хотите удалить продавца?',
      content: 'Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return new Promise((resolve) => {
          setProductsAction().then(resolve)
        })
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const changeSellerStatus = async (e: MenuInfo) => {
    if (props.mode === 'edit') {
      try {
        await sellerApi.changeSellerStatus(props.sellerId, {
          status: e.key as ESellerStatus.APPROVED | ESellerStatus.DECLINED
        })
        setSellerStatus(e.key as ESellerStatus)
      } catch (e) {
        showServerError(e)
      }
    }
  }

  const onChangeSellerHolidays = async (checked: boolean) => {
    try {
      setIsHolidaysSaving(true)
      const response = await sellerApi.updateSellerHolidays({
        holidaysIsActive: checked,
        seller_id:
          props.mode === 'edit' && props.type === 'seller'
            ? props.sellerId
            : undefined
      })
      form.setFieldValue('holidaysIsActive', response.data.holidaysIsActive)
      message.success(
        response.data.holidaysIsActive
          ? 'Режим отпуска включен'
          : 'Режим отпуска выключен'
      )
    } catch (e) {
      form.setFieldValue('holidaysIsActive', !checked)
      showServerError(e)
    } finally {
      setIsHolidaysSaving(false)
    }
  }

  // Комиссия для показа продавцу: сниженная ставка, если акция активна и не истекла, иначе базовая
  const discountRateNumber =
    commissionDiscountRate !== ''
      ? normalizeNumber(commissionDiscountRate)
      : NaN
  const isCommissionDiscountEffective =
    commissionDiscountActive &&
    !Number.isNaN(discountRateNumber) &&
    discountRateNumber < currentCommission &&
    (!commissionDiscountEndDate ||
      commissionDiscountEndDate.endOf('day').isAfter(dayjs()))
  const displayedCommission = isCommissionDiscountEffective
    ? discountRateNumber
    : currentCommission

  // Закрытые реквизиты бэкенд отдаёт сотруднику пустыми. В форму кладём '', а не null:
  // поле с маской, у которого заполненное значение сменилось на null, зацикливает рендер
  const setEmployeeSectionFields = (
    data: ISeller,
    sections: TEmployeeAccessSection[]
  ) => {
    const values: Record<TEmployeeAccessSection, Record<string, unknown>> = {
      BANKING: {
        bankName: data.banking_details.name ?? '',
        bankBIK: data.banking_details.BIK ?? '',
        bankPC: data.banking_details.PC ?? '',
        bankKC: data.banking_details.KC ?? '',
        cardNumber: data.additional_info?.card_number ?? ''
      },
      SHOP: {
        shopName: data.shop_details.shop_name,
        defaultTax: data.default_tax ?? undefined,
        logo: toUploadedImage(data.shop_details.logo),
        banner: toUploadedImage(data.shop_details.banner),
        desc: data.shop_details.desc
      }
    }
    form.setFields(
      sections.flatMap((section) =>
        Object.entries(values[section]).map(([name, value]) => ({
          name,
          value,
          errors: []
        }))
      )
    )
  }

  const loadEmployeeSections = async (sections: TEmployeeAccessSection[]) => {
    try {
      const { data } = await sellerApi.getSeller(editSellerId)
      setEmployeeSectionFields(data, sections)
      return true
    } catch (e) {
      showServerError(e)
      return false
    }
  }

  // Раздел открываем только с загруженными значениями: пустые реквизиты в открытой
  // форме сотрудник сохранил бы поверх настоящих
  const onEmployeeAccessGranted = async (
    section: TEmployeeAccessSection,
    until: string
  ) => {
    if (await loadEmployeeSections([section])) {
      setEmployeeAccess((prev) => ({ ...prev, [section]: until }))
    }
  }

  const closeEmployeeSections = (sections: TEmployeeAccessSection[]) => {
    if (!sections.length) return
    setEmployeeAccess((prev) =>
      sections.reduce((acc, section) => ({ ...acc, [section]: null }), prev)
    )
    if (sections.includes('BANKING')) {
      form.setFields(
        EMPLOYEE_SECTION_FIELDS.BANKING.map((name) => ({
          name,
          value: '',
          errors: []
        }))
      )
    }
    // данные магазина видны и закрытыми — несохранённые правки меняем на сохранённое
    if (sections.includes('SHOP')) loadEmployeeSections(['SHOP'])
  }

  // Закрываем по часам браузера, сервер не ждём: если он закрыл раздел раньше,
  // сохранение вернёт 403
  React.useEffect(() => {
    const expiresAt = (section: TEmployeeAccessSection) =>
      new Date(employeeAccess[section] ?? 0).getTime()
    const openSections = EMPLOYEE_SECTIONS.filter(
      (section) => employeeAccess[section]
    )
    if (!isEmployeeMode || !openSections.length) return

    const nearest = Math.min(...openSections.map(expiresAt))
    const timeout = setTimeout(
      () =>
        closeEmployeeSections(
          openSections.filter((section) => expiresAt(section) <= Date.now())
        ),
      Math.max(nearest - Date.now(), 0) + 500
    )
    return () => clearTimeout(timeout)
  }, [employeeAccess, isEmployeeMode])

  const onEmployeeSubmit = async () => {
    const openSections = EMPLOYEE_SECTIONS.filter(
      (section) => employeeAccess[section]
    )
    try {
      const fields = openSections.flatMap(
        (section) => EMPLOYEE_SECTION_FIELDS[section]
      )
      if (fields.length) await form.validateFields(fields)
      setIsSubmitting(true)
      const { data } = await sellerEmployeesApi.updateSeller(
        getEmployeeSubmitData(
          form.getFieldsValue(true),
          editSellerId,
          openSections
        )
      )
      // сохранение закрывает открытые разделы: реквизиты снова приходят пустыми
      setEmployeeSectionFields(data, openSections)
      setDisableInfo(data)
      setEmployeeAccess({ BANKING: null, SHOP: null })
      message.info('Данные успешно обновлены')
    } catch (e: any) {
      if (e.errorFields) {
        return showMessage('error', 'Заполните все поля корректно', '')
      }
      if (e.response?.status === 403) closeEmployeeSections(openSections)
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmit = async () => {
    if (isEmployeeMode) return onEmployeeSubmit()
    try {
      await form.validateFields()
      // Почта — логин кабинета: новую бэкенд принимает только после кода с неё
      if (isProfile && currentEmail !== savedEmail) {
        return showMessage(
          'error',
          'Новая почта не подтверждена',
          'Нажмите «Подтвердить» рядом с почтой и введите код из письма'
        )
      }
      const data: ISellerFormData = form.getFieldsValue(true)
      setIsSubmitting(true)
      if (props.mode === 'create') {
        await sellerApi.addSeller(getLegalSubmitData(data))
        message.success('Продавец успешно добавлен')
      } else {
        const finalData = getEditSubmitData(data, props.sellerId, deletedPhotos)
        const { data: updatedSeller } = await sellerApi.updateSellerInfo(finalData)
        setDisableInfo(updatedSeller)
        message.info(
          props.type === 'seller'
            ? 'Продавец успешно обновлен'
            : 'Данные успешно обновлены'
        )
      }
    } catch (e: any) {
      if (e.errorFields) {
        return showMessage('error', 'Заполните все поля корректно', '')
      }
      if (e.response?.data?.message === 'Организация не действительна') {
        form.setFields([
          {
            name: 'legalINN',
            errors: [e.response.data.message]
          }
        ])
      }
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFormChange = (data: Partial<ISellerFormData>) => {
    if (data.hasOwnProperty('legalINN') && companyInfo) {
      setCompanyInfo(null)
    }
  }

  const handleToggleExpandedCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleCategoryHiddenChange = (categoryId: string, checked: boolean) => {
    setHiddenCategoryIds((prev) =>
      checked
        ? Array.from(new Set([...prev, categoryId]))
        : prev.filter((id) => id !== categoryId)
    )
  }

  const handleSubCategoryHiddenChange = (
    subCategoryId: string,
    checked: boolean
  ) => {
    setHiddenSubCategoryIds((prev) =>
      checked
        ? Array.from(new Set([...prev, subCategoryId]))
        : prev.filter((id) => id !== subCategoryId)
    )
  }

  const handleShowAllCategories = () => {
    setHiddenCategoriesFilter('all')
    setHiddenCategoryIds([])
    setHiddenSubCategoryIds([])
  }

  const handleHideAllCategories = () => {
    setHiddenCategoriesFilter('hideAll')
    setHiddenCategoryIds(hiddenCategoryNodes.map((category) => category.id))
    setHiddenSubCategoryIds(
      hiddenCategoryNodes.flatMap((category) =>
        category.children.map((subCategory) => subCategory.id)
      )
    )
  }

  const mapHiddenRecords = React.useCallback(
    (
      records: ISellerHiddenCategoryRecord[]
    ): { hiddenCategories: string[]; hiddenSubCategories: string[] } => {
      const hiddenCategories = new Set<string>()
      const hiddenSubCategories = new Set<string>()

      records.forEach((record) => {
        const categoryId = normalizeNodeId(record.category_id)
        const subCategoryId = normalizeNodeId(record.subCategory_id)

        if (subCategoryId) {
          hiddenSubCategories.add(subCategoryId)
          return
        }

        if (categoryId) {
          hiddenCategories.add(categoryId)
        }
      })

      return {
        hiddenCategories: Array.from(hiddenCategories),
        hiddenSubCategories: Array.from(hiddenSubCategories)
      }
    },
    [normalizeNodeId]
  )

  const loadHiddenCategoriesSettings = React.useCallback(async () => {
    try {
      setIsHiddenCategoriesLoading(true)

      const [categoriesResponse, hiddenResponse] = await Promise.all([
        productApi.getCategories({ parsed: 'true' }),
        props.type === 'seller'
          ? sellerApi.getAdminSellerHiddenCategories(editSellerId)
          : sellerApi.getSellerHiddenCategories()
      ])

      const nodes = toHiddenCategoryNodes(
        categoriesResponse.data as IParsedCategory[]
      )
      const parsedHidden = mapHiddenRecords(hiddenResponse.data)

      setHiddenCategoryNodes(nodes)
      setExpandedCategories(nodes.map((item) => item.id))
      setHiddenCategoryIds(parsedHidden.hiddenCategories)
      setHiddenSubCategoryIds(parsedHidden.hiddenSubCategories)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsHiddenCategoriesLoading(false)
    }
  }, [editSellerId, mapHiddenRecords, props.type, toHiddenCategoryNodes])

  const openHiddenCategoriesSettings = async () => {
    setHiddenCategoriesFilter('none')
    setHiddenCategoriesSearch('')
    setHiddenCategoryNodes([])
    setExpandedCategories([])
    setHiddenCategoryIds([])
    setHiddenSubCategoryIds([])
    setIsHiddenCategoriesOpen(true)
    await loadHiddenCategoriesSettings()
  }

  const persistHiddenCategories = async (
    payload: ISaveSellerHiddenCategoriesRequest
  ) => {
    if (props.type === 'seller') {
      await sellerApi.saveAdminSellerHiddenCategories(editSellerId, payload)
    } else {
      await sellerApi.saveSellerHiddenCategories(payload)
    }
    message.success('Настройки выдачи сохранены')
    setIsHiddenCategoriesOpen(false)
  }

  // Скрытие подкатегории снимает с витрины все опубликованные товары продавца
  // в ней. Число показываем до сохранения: о своём скрытии продавец
  // уведомления не получает
  const saveHiddenCategoriesSettings = async () => {
    const payload = {
      hidden_categories: hiddenCategoryIds,
      hidden_subcategories: hiddenSubCategoryIds
    }
    try {
      setIsHiddenCategoriesSaving(true)
      const preview =
        props.type === 'seller'
          ? sellerApi.previewAdminSellerHiddenCategories(editSellerId, payload)
          : sellerApi.previewSellerHiddenCategories(payload)
      const { data } = await preview

      if (data.disabled_count === 0) {
        await persistHiddenCategories(payload)
        return
      }

      Modal.confirm({
        centered: true,
        title: 'Снять товары с витрины?',
        content:
          props.type === 'seller'
            ? `Будут отключены опубликованные товары и вариации продавца в скрытых подкатегориях: ${data.disabled_count}. Продавец получит уведомление со списком. Товары не удаляются — вернутся, когда подкатегорию снова покажут.`
            : `Будут отключены ваши опубликованные товары и вариации в скрытых подкатегориях: ${data.disabled_count}. Товары не удаляются — вернутся, когда вы снова покажете подкатегорию.`,
        icon: <ExclamationCircleOutlined />,
        okText: 'Скрыть и отключить',
        cancelText: 'Отменить',
        onOk: () => persistHiddenCategories(payload).catch(showServerError)
      })
    } catch (e) {
      showServerError(e)
    } finally {
      setIsHiddenCategoriesSaving(false)
    }
  }

  return (
    <>
      {isLoading || isError ? null : (
        <Form
          form={form}
          name='sellers'
          size='large'
          autoComplete='off'
          onValuesChange={onFormChange}
        >
          <section className={styles.section}>
            <div
              className={styles.header}
              style={{ gap: props.type === 'seller' ? 15 : 0 }}
            >
              <SectionTitle
                level={2}
                className={classNames(styles.sectionTitle, styles.title)}
              >
                {isEmployeeMode
                  ? 'Информация о сотруднике'
                  : 'Информация о продавце'}
              </SectionTitle>
              <div className={styles.status}>
                {props.type === 'seller' &&
                  sellerStatus &&
                  (sellerStatus !== ESellerStatus.WAITING_APPROVE ? (
                    <Tag color={sellerStatuses[sellerStatus].color}>
                      {sellerStatuses[sellerStatus].label}
                    </Tag>
                  ) : (
                    <StatusDropdown
                      items={getSellerStatusItems(sellerStatus)}
                      label={sellerStatuses[sellerStatus].label}
                      color={sellerStatuses[sellerStatus].color}
                      onClick={(e) => changeSellerStatus(e)}
                    />
                  ))}
              </div>
            </div>
            {props.mode === 'edit' && (
              <>
                <div className={styles.toggles}>
                  {(props.type === 'profile' || props.type === 'seller') && (
                    <div className={styles.toggleRow}>
                      <Form.Item className={styles.toggleItem} name='holidaysIsActive' valuePropName='checked'>
                        <Switch loading={isHolidaysSaving} onChange={onChangeSellerHolidays} />
                      </Form.Item>
                      <Typography.Text>Срок доставки увеличен</Typography.Text>
                    </div>
                  )}
                  <div className={styles.toggleRow}>
                    <Form.Item className={styles.toggleItem}>
                      <Switch
                        checked={Boolean(disableInfo?.is_disabled)}
                        loading={isHiddenSaving}
                        onChange={onHiddenChange}
                      />
                    </Form.Item>
                    <Typography.Text>
                      {isProfile ? 'Скрыть все мои товары' : 'Скрыть товары продавца'}
                    </Typography.Text>
                    {disableInfo && <SellerDisableStatus seller={disableInfo} hideActive />}
                  </div>
                </div>
                <div className={styles.hiddenCategoriesRow}>
                  <Typography.Text>Скрыть категории:</Typography.Text>
                  <Button
                    icon={<SlidersTwoTone twoToneColor='#1677FF' />}
                    className={styles.hiddenCategoriesButton}
                    onClick={openHiddenCategoriesSettings}
                  >
                    Настроить
                  </Button>
                </div>
                <Typography.Text className={styles.hiddenCategoriesHint}>
                  Товары не удаляются.
                </Typography.Text>
              </>
            )}
            {isCreate ? (
              <Form.Item
                name='legalType'
                label='Выберите тип лица'
                rules={[
                  {
                    required: true,
                    message: 'Выберите тип лица'
                  }
                ]}
                rootClassName={styles.sellerType}
                hasFeedback
              >
                <Select
                  placeholder='Выберите тип лица'
                  // ИП и юрлицо заводятся одним типом LEGAL, различает их DaData по ИНН
                  onChange={() => form.setFieldValue('type', ESellerTypes.LEGAL)}
                  options={[
                    {
                      value: ESellerLegalTypes.INDIVIDUAL,
                      label: 'Индивидуальный предприниматель'
                    },
                    { value: ESellerLegalTypes.LEGAL, label: 'Юридическое лицо' }
                  ]}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name='type'
                label='Выберите тип лица'
                hidden={isEmployeeMode}
                rules={[
                  {
                    required: true,
                    message: 'Выберите тип лица'
                  }
                ]}
                rootClassName={styles.sellerType}
                hasFeedback
              >
                <Select
                  placeholder='Выберите тип лица'
                  options={[
                    { value: ESellerTypes.LEGAL, label: 'Юридическое лицо' },
                    // физлиц больше не заводим, подпись нужна для заведённых раньше
                    ...(sellerType === ESellerTypes.PHYSICAL
                      ? [
                        {
                          value: ESellerTypes.PHYSICAL,
                          label: 'Физическое лицо'
                        }
                      ]
                      : [])
                  ]}
                  disabled={isProfile}
                />
              </Form.Item>
            )}
            {isEmployeeMode && (
              <>
                <Form.Item label='Имя' tooltip={EMPLOYEE_NAME_HINT}>
                  <Input value={employee?.name} disabled />
                </Form.Item>
                <Form.Item label='Фамилия' tooltip={EMPLOYEE_NAME_HINT}>
                  <Input value={employee?.surname} disabled />
                </Form.Item>
                <Form.Item label='Почта'>
                  <Input value={employee?.email} disabled />
                </Form.Item>
              </>
            )}
            {sellerType && !isEmployeeMode && (
              <>
                <Form.Item
                  name='firstName'
                  label='Имя'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните имя'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните имя'
                    },
                    ruNamePattern
                  ]}
                  hasFeedback
                >
                  <Input
                    placeholder='Введите имя'
                    disabled={isProfile && !isLegalType}
                  />
                </Form.Item>
                <Form.Item
                  name='lastName'
                  label='Фамилия'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните фамилию'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните фамилию'
                    },
                    ruNamePattern
                  ]}
                  hasFeedback
                >
                  <Input
                    placeholder='Введите фамилию'
                    disabled={isProfile && !isLegalType}
                  />
                </Form.Item>
                <div className={styles.middleName}>
                  <Form.Item
                    name='middleName'
                    label='Отчество'
                    rules={
                      noMiddleName
                        ? []
                        : [
                          {
                            required: true,
                            message: 'Заполните отчество'
                          },
                          {
                            whitespace: true,
                            message: 'Заполните отчество'
                          },
                          ruNamePattern
                        ]
                    }
                    style={{ flexGrow: 1 }}
                    hasFeedback
                  >
                    <Input
                      placeholder='Введите отчество'
                      disabled={isProfile && !isLegalType}
                    />
                  </Form.Item>
                  <Form.Item
                    name='noMiddleName'
                    label='Нет отчества'
                    valuePropName='checked'
                  >
                    <Switch disabled={isProfile && !isLegalType} />
                  </Form.Item>
                </div>
                <Form.Item
                  name='email'
                  label='Почта'
                  rules={[
                    {
                      type: 'email',
                      message: 'Некорректная почта'
                    },
                    {
                      required: true,
                      message: 'Заполните почту'
                    }
                  ]}
                >
                  <Input
                    placeholder='Введите почтовый адрес'
                    type='email'
                    suffix={
                      isCreate ? undefined : (
                        <ContactVerificationBadge
                          isVerified={isEmailConfirmed}
                          isLoading={isSendingEmailCode}
                          onConfirm={isProfile ? confirmEmail : undefined}
                        />
                      )
                    }
                  />
                </Form.Item>
                {props.mode === 'create' && (
                  <Form.Item
                    name='password'
                    label='Пароль'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните пароль'
                      },
                      ...passwordPattern
                    ]}
                    hasFeedback
                  >
                    <Input.Password placeholder='Введите пароль' />
                  </Form.Item>
                )}
                <Form.Item
                  name='phone'
                  label='Номер телефона'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните номер телефона'
                    },
                    {
                      min: 18,
                      message: 'Заполните номер телефона'
                    }
                  ]}
                >
                  <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
                    {/* @ts-ignore */}
                    {() => (
                      <Input
                        placeholder='Введите номер телефона'
                        suffix={
                          isCreate ? undefined : (
                            <ContactVerificationBadge
                              isVerified={isPhoneConfirmed}
                              isLoading={isSendingPhoneCode}
                              onConfirm={isProfile ? confirmPhone : undefined}
                            />
                          )
                        }
                      />
                    )}
                  </InputMask>
                </Form.Item>
                <Form.Item name='edoOperator' label='Оператор ЭДО'>
                  <Input placeholder='Введите оператора' />
                </Form.Item>
                <Form.Item name='edoId' label='ID ЭДО'>
                  <Input placeholder='Введите идентификационный номер' />
                </Form.Item>
              </>
            )}
            {sellerType === ESellerTypes.PHYSICAL && !isEmployeeMode && (
              <>
                <Form.Item
                  name='registrationAddress'
                  label='Адрес регистрации'
                  hasFeedback
                >
                  <Input placeholder='Введите адрес регистрации' />
                </Form.Item>
                <Form.Item
                  name='actualAddress'
                  label='Фактический адрес'
                  hasFeedback
                >
                  <Input placeholder='Введите фактический адрес' />
                </Form.Item>
                <div className={styles.passportData}>
                  <Form.Item
                    name='passportData'
                    label='Паспортные данные'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните серию и номер паспорта'
                      },
                      {
                        min: 12,
                        message: 'Заполните серию и номер паспорта'
                      }
                    ]}
                    className={styles.passportDataField}
                    hasFeedback
                  >
                    <InputMask
                      mask='99 99 999999'
                      maskChar={null}
                      disabled={isProfile}
                    >
                      {/* @ts-ignore */}
                      {() => (
                        <Input
                          placeholder='__ __ ______'
                          disabled={isProfile}
                        />
                      )}
                    </InputMask>
                  </Form.Item>
                  <Form.Item
                    name='birthDate'
                    label='Дата рождения'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните дату рождения'
                      }
                    ]}
                    rootClassName={styles.dateField}
                  >
                    <DatePicker
                      format='DD/MM/YYYY'
                      placeholder='19/02/2002'
                      disabled={isProfile}
                      inputReadOnly
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name='passportPhoto'
                  valuePropName='fileList'
                  getValueFromEvent={normalizeFile}
                  label='Фото паспорта'
                  rules={[
                    {
                      required: true,
                      message: 'Загрузите фотографию паспорта'
                    }
                  ]}
                  rootClassName={classNames({
                    [styles.passportPhotoWrapper]: isPassportPhoto
                  })}
                >
                  <Upload
                    accept='image/png, image/jpeg, image/jpg'
                    maxCount={1}
                    customRequest={() => false}
                    disabled={isProfile}
                    beforeUpload={(file) => {
                      const isValid =
                        file.type === 'image/png' ||
                        file.type === 'image/jpg' ||
                        file.type === 'image/jpeg'
                      if (!isValid) {
                        showMessage(
                          'error',
                          'Поддерживаются только форматы JPG, JPEG, PNG',
                          ''
                        )
                      } else {
                        message.success('Файл успешно загружен')
                      }
                      return isValid || Upload.LIST_IGNORE
                    }}
                    itemRender={(_, file, fileList, { remove }) => {
                      return (
                        <div className={styles.passportPhoto}>
                          <PaperClipOutlined style={{ opacity: 0.45 }} />
                          {file.uid ? (
                            <Typography.Link
                              href={`${IMAGES_URL}${file.uid}?access_token=${token}`}
                              target='_blank'
                            >
                              passport
                            </Typography.Link>
                          ) : (
                            <Typography.Text
                              style={{ color: '#1677FF' }}
                            >{`passport.${
                                file.type?.split('/')[1]
                              }`}</Typography.Text>
                          )}
                          {!isProfile && (
                            <CloseOutlined
                              className={styles.passportPhotoRemove}
                              onClick={() => {
                                remove()
                                if (!file.originFileObj) {
                                  setDeletedPhotos((prev) => [
                                    ...prev,
                                    file.uid
                                  ])
                                }
                              }}
                            />
                          )}
                        </div>
                      )
                    }}
                  >
                    <Button
                      size='middle'
                      disabled={isProfile}
                      icon={<UploadOutlined />}
                    >
                      Загрузить фото
                    </Button>
                  </Upload>
                </Form.Item>
                <Form.Item
                  name='physicalINN'
                  label='ИНН'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните ИНН'
                    },
                    {
                      min: 12,
                      message: 'Заполните ИНН корректно'
                    }
                  ]}
                  hasFeedback
                >
                  <InputMask
                    mask='999999999999'
                    maskChar={null}
                    disabled={isProfile}
                  >
                    {/* @ts-ignore */}
                    {() => (
                      <Input placeholder='Введите ИНН' disabled={isProfile} />
                    )}
                  </InputMask>
                </Form.Item>
              </>
            )}
          </section>
          {isLegalType && (
            <section className={styles.section}>
              <Typography.Title level={2} className={styles.sectionTitle}>
                Сведения о компании
              </Typography.Title>
              <div
                className={classNames({ [styles.legalINNWrapper]: isCreate })}
              >
                <Form.Item
                  name='legalINN'
                  label='ИНН'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните ИНН'
                    },
                    {
                      min: 10,
                      message: 'Заполните ИНН корректно'
                    },
                    {
                      validator: (_, value) =>
                        value.length === 11
                          ? Promise.reject(new Error('Заполните ИНН корректно'))
                          : Promise.resolve()
                    }
                  ]}
                  style={isCreate ? { marginBottom: 0, flex: 1 } : {}}
                  hasFeedback
                >
                  <InputMask
                    mask='999999999999'
                    maskChar={null}
                    disabled={isProfile && isLegalType}
                  >
                    {/* @ts-ignore */}
                    {() => (
                      <Input
                        placeholder='Введите ИНН'
                        disabled={isProfile && isLegalType}
                        maxLength={12}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && isCreate) {
                            checkLegalINN()
                          }
                        }}
                      />
                    )}
                  </InputMask>
                </Form.Item>
                {isCreate && (
                  <Tooltip
                    title='Проверить и получить сведения о компании'
                    trigger='hover'
                  >
                    <Button
                      type='primary'
                      size='small'
                      icon={<LoginOutlined />}
                      rootClassName={styles.getCompanyInfo}
                      onClick={checkLegalINN}
                      loading={isLegalINNChecking}
                    />
                  </Tooltip>
                )}
              </div>
              {isCreate ? (
                <>
                  <Form.Item
                    label='Название компании'
                    className={styles.disabledField}
                  >
                    <Input
                      value={companyInfo?.companyName ?? ''}
                      placeholder='Название компании'
                      disabled
                    />
                  </Form.Item>
                  <Form.Item label='ОКПО' className={styles.disabledField}>
                    <Input
                      value={companyInfo?.OKPO ?? ''}
                      placeholder='ОКПО'
                      disabled
                    />
                  </Form.Item>
                  <Form.Item label='КПП' className={styles.disabledField}>
                    <Input
                      value={companyInfo?.legalKPP ?? ''}
                      placeholder='КПП'
                      disabled
                    />
                  </Form.Item>
                  <Form.Item label='ОГРН' className={styles.disabledField}>
                    <Input
                      value={companyInfo?.OGRN ?? ''}
                      placeholder='ОГРН'
                      disabled
                    />
                  </Form.Item>
                  <Form.Item
                    label='Тип организации'
                    className={styles.disabledField}
                  >
                    <Select
                      value={companyInfo?.organizationType}
                      placeholder='Выберите тип организации'
                      suffixIcon={null}
                      disabled
                      options={[
                        {
                          value: ESellerLegalTypes.LEGAL,
                          label: 'Юридическое лицо'
                        },
                        {
                          value: ESellerLegalTypes.INDIVIDUAL,
                          label: 'Индивидуальный предприниматель'
                        }
                      ]}
                      className={classNames({
                        [styles.disableSelectField]:
                          companyInfo?.organizationType
                      })}
                    />
                  </Form.Item>
                  <Form.Item
                    label='Юридический адрес'
                    className={styles.disabledField}
                  >
                    <Input
                      value={companyInfo?.legalAddress ?? ''}
                      placeholder='Юридический адрес'
                      disabled
                    />
                  </Form.Item>
                  <Form.Item
                    name='actualAddress'
                    label='Фактический адрес'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните фактический адрес'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните фактический адрес'
                      }
                    ]}
                    hasFeedback
                  >
                    <Input
                      placeholder='Фактический адрес'
                      disabled={isAddressDisabled('actualAddress')}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    name='companyName'
                    label='Название компании'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните название компании'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните название компании'
                      }
                    ]}
                    hasFeedback
                  >
                    <Input
                      placeholder='Название компании'
                      disabled={isProfile}
                    />
                  </Form.Item>
                  <Form.Item
                    name='OKPO'
                    label='ОКПО'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните ОКПО'
                      },
                      {
                        min: 8,
                        message: 'Заполните ОКПО корректно'
                      }
                    ]}
                    hasFeedback
                  >
                    <InputMask
                      mask='9999999999'
                      disabled={isProfile}
                      maskChar={null}
                    >
                      {/* @ts-ignore */}
                      {() => <Input disabled={isProfile} placeholder='ОКПО' />}
                    </InputMask>
                  </Form.Item>
                  <Form.Item
                    name='legalKPP'
                    label='КПП'
                    rules={[
                      {
                        min: 9,
                        message: 'Заполните КПП корректно'
                      }
                    ]}
                    hasFeedback
                  >
                    <InputMask
                      mask='999999999'
                      disabled={isProfile}
                      maskChar={null}
                    >
                      {/* @ts-ignore */}
                      {() => <Input placeholder='КПП' disabled={isProfile} />}
                    </InputMask>
                  </Form.Item>
                  <Form.Item
                    name='OGRN'
                    label='ОГРН'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните ОГРН'
                      },
                      {
                        min: 13,
                        message: 'Заполните ОГРН корректно'
                      }
                    ]}
                    hasFeedback
                  >
                    <InputMask
                      mask='9999999999999'
                      disabled={isProfile}
                      maskChar={null}
                    >
                      {/* @ts-ignore */}
                      {() => <Input placeholder='ОГРН' disabled={isProfile} />}
                    </InputMask>
                  </Form.Item>
                  <Form.Item
                    name='organizationType'
                    label='Тип организации'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите тип организации'
                      }
                    ]}
                    hasFeedback
                  >
                    <Select
                      placeholder='Выберите тип организации'
                      disabled={isProfile}
                      suffixIcon={isProfile ? null : <DownOutlined />}
                      options={[
                        {
                          value: ESellerLegalTypes.LEGAL,
                          label: 'Юридическое лицо'
                        },
                        {
                          value: ESellerLegalTypes.INDIVIDUAL,
                          label: 'Индивидуальный предприниматель'
                        }
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name='legalAddress'
                    label='Юридический адрес'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните юридический адрес'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните юридический адрес'
                      }
                    ]}
                    hasFeedback
                  >
                    <Input
                      placeholder='Юридический адрес'
                      disabled={isAddressDisabled('legalAddress')}
                    />
                  </Form.Item>
                  <Form.Item
                    name='actualAddress'
                    label='Фактический адрес'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните фактический адрес'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните фактический адрес'
                      }
                    ]}
                    hasFeedback
                  >
                    <Input
                      placeholder='Фактический адрес'
                      disabled={isAddressDisabled('actualAddress')}
                    />
                  </Form.Item>
                </>
              )}
            </section>
          )}
          {sellerType && (
            <>
              <section id='banking-details' className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Typography.Title
                    level={2}
                    className={classNames(styles.sectionTitle, styles.title)}
                  >
                    Банковские сведения
                  </Typography.Title>
                  {isEmployeeMode && (
                    <EmployeeSectionAccess
                      section='BANKING'
                      until={employeeAccess.BANKING}
                      onGranted={onEmployeeAccessGranted}
                    />
                  )}
                </div>
                <Form.Item
                  name='bankName'
                  label='Наименование банка'
                  rules={
                    isLegalType
                      ? [
                        {
                          required: true,
                          message: 'Заполните наименование банка'
                        },
                        {
                          whitespace: true,
                          message: 'Заполните наименование банка'
                        }
                      ]
                      : []
                  }
                  hasFeedback
                >
                  <Input
                    placeholder={getBankingPlaceholder('Введите название банка')}
                    disabled={isBankingLocked}
                  />
                </Form.Item>
                <Form.Item
                  name='bankBIK'
                  label='БИК'
                  rules={
                    isLegalType
                      ? [
                        {
                          required: true,
                          message: 'Заполните БИК'
                        },
                        {
                          min: 9,
                          message: 'Заполните БИК корректно'
                        }
                      ]
                      : [
                        {
                          min: 9,
                          message: 'Заполните БИК корректно'
                        }
                      ]
                  }
                  hasFeedback
                >
                  <InputMask
                    mask='999999999'
                    maskChar={null}
                    disabled={isBankingLocked}
                  >
                    {/* @ts-ignore */}
                    {() => (
                      <Input
                        placeholder={getBankingPlaceholder('Введите БИК')}
                        disabled={isBankingLocked}
                      />
                    )}
                  </InputMask>
                </Form.Item>
                {sellerType === ESellerTypes.PHYSICAL && !isEmployeeMode && (
                  <Form.Item
                    name='physicalKPP'
                    label='КПП'
                    rules={[{ min: 9, message: 'Заполните КПП корректно' }]}
                    hasFeedback
                  >
                    <InputMask mask='999999999' maskChar={null}>
                      {/* @ts-ignore */}
                      {() => <Input placeholder='Введите КПП' />}
                    </InputMask>
                  </Form.Item>
                )}
                <Form.Item
                  name='bankPC'
                  label='РС'
                  rules={
                    isLegalType
                      ? [
                        {
                          required: true,
                          message: 'Заполните расчетный счет'
                        },
                        {
                          min: 20,
                          message: 'Заполните расчетный счет корректно'
                        }
                      ]
                      : [
                        {
                          min: 20,
                          message: 'Заполните расчетный счет корректно'
                        }
                      ]
                  }
                  hasFeedback
                >
                  <InputMask
                    mask='99999999999999999999'
                    maskChar={null}
                    disabled={isBankingLocked}
                  >
                    {/* @ts-ignore */}
                    {() => (
                      <Input
                        placeholder={getBankingPlaceholder('Введите расчетный счет')}
                        disabled={isBankingLocked}
                      />
                    )}
                  </InputMask>
                </Form.Item>
                <Form.Item
                  name='bankKC'
                  label='КС'
                  rules={
                    isLegalType
                      ? [
                        {
                          required: true,
                          message: 'Заполните корпоративный счет'
                        },
                        {
                          min: 20,
                          message: 'Заполните корпоративный счет корректно'
                        }
                      ]
                      : [
                        {
                          min: 20,
                          message: 'Заполните корпоративный счет корректно'
                        }
                      ]
                  }
                  hasFeedback
                >
                  <InputMask
                    mask='99999999999999999999'
                    maskChar={null}
                    disabled={isBankingLocked}
                  >
                    {/* @ts-ignore */}
                    {() => (
                      <Input
                        placeholder={getBankingPlaceholder('Введите корпоративный счет')}
                        disabled={isBankingLocked}
                      />
                    )}
                  </InputMask>
                </Form.Item>
                {sellerType === ESellerTypes.PHYSICAL && (
                  <Form.Item
                    name='cardNumber'
                    label='Номер карты'
                    rules={[
                      { min: 19, message: 'Заполните номер карты корректно' }
                    ]}
                    hasFeedback
                  >
                    <InputMask
                      mask='9999 9999 9999 9999'
                      maskChar={null}
                      disabled={isBankingLocked}
                    >
                      {/* @ts-ignore */}
                      {() => (
                        <Input
                          placeholder={getBankingPlaceholder('Введите номер карты')}
                          disabled={isBankingLocked}
                        />
                      )}
                    </InputMask>
                  </Form.Item>
                )}
              </section>
              <section id='shop-details' className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Typography.Title
                    level={2}
                    className={classNames(styles.sectionTitle, styles.title)}
                  >
                    Данные о магазине
                  </Typography.Title>
                  {isEmployeeMode && (
                    <EmployeeSectionAccess
                      section='SHOP'
                      until={employeeAccess.SHOP}
                      onGranted={onEmployeeAccessGranted}
                    />
                  )}
                </div>
                <Form.Item
                  name='shopName'
                  label='Название магазина'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните название магазина'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните название магазина'
                    }
                  ]}
                  hasFeedback
                >
                  <Input
                    placeholder='Введите название магазина'
                    disabled={isShopLocked}
                  />
                </Form.Item>
                <Form.Item
                  name='defaultTax'
                  label='Ставка НДС по умолчанию'
                  tooltip='Подставляется в новые товары: в карточку и при импорте из 1С и InSales. У уже созданных товаров ставка не меняется.'
                  rules={
                    isProfile
                      ? [
                        {
                          required: true,
                          message: 'Выберите ставку НДС'
                        }
                      ]
                      : []
                  }
                  hasFeedback
                >
                  <Select
                    placeholder='Выберите ставку'
                    options={TAX_OPTIONS}
                    disabled={isShopLocked}
                  />
                </Form.Item>
                {isProfile && (
                  <Form.Item label='Комиссия магазина:'>
                    <div className={styles.commissionRow}>
                      <Input
                        className={styles.commissionHalf}
                        value={`${displayedCommission}%`}
                        disabled
                      />
                      {isCommissionDiscountEffective && (
                        <div className={classNames(styles.commissionHalf, styles.commissionNotice)}>
                          {commissionDiscountEndDate
                            ? `Снижена до ${commissionDiscountEndDate.format('DD.MM.YYYY')}`
                            : 'Снижена'}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                )}
                <Typography.Title level={4} style={{ marginBottom: 3 }}>
                  Логотип:
                </Typography.Title>
                <Typography.Text className={styles.subTitle}>
                  Рекомендованный размер логотипа не менее <br />
                  250px по ширине. Поддерживаются форматы JPG, JPEG, PNG,
                  WebP.
                </Typography.Text>
                <Uploader
                  name='logo'
                  disabled={!!logo.length || isShopLocked}
                  readOnly={isShopLocked}
                  rules={[
                    {
                      required: true,
                      message: 'Добавьте логотип'
                    }
                  ]}
                  onRemoveClick={(file) => {
                    if (!file.originFileObj) {
                      setDeletedPhotos((prev) => [...prev, file?.uid ?? ''])
                    }
                  }}
                />
                <Typography.Title level={4} style={{ marginBottom: 3 }}>
                  Баннер магазина:
                </Typography.Title>
                <Typography.Text className={styles.subTitle}>
                  Рекомендованное соотношение сторон баннера 4:1 (1440х360,
                  720х180, ...) <br />
                  Поддерживаются форматы JPG, JPEG, PNG, WebP.
                </Typography.Text>
                <Uploader
                  name='banner'
                  disabled={!!banner.length || isShopLocked}
                  readOnly={isShopLocked}
                  rules={[
                    {
                      required: true,
                      message: 'Добавьте баннер магазина'
                    }
                  ]}
                  onRemoveClick={(file) => {
                    if (!file.originFileObj) {
                      setDeletedPhotos((prev) => [...prev, file.uid])
                    }
                  }}
                />
                <Typography.Title level={4} style={{ marginBottom: 22 }}>
                  Описание магазина:
                </Typography.Title>
                <Form.Item
                  name='desc'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните описание магазина'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните описание магазина'
                    }
                  ]}
                  hasFeedback
                >
                  <Input.TextArea
                    placeholder='Описание магазина'
                    rows={2}
                    disabled={isShopLocked}
                  />
                </Form.Item>
                <div className={styles.footer}>
                  {!savePortalTarget && (
                    <Button
                      htmlType='button'
                      type='primary'
                      size='large'
                      icon={<SaveOutlined />}
                      loading={isSubmitting}
                      onClick={onSubmit}
                      disabled={isLegalINNChecking}
                    >
                      Сохранить
                    </Button>
                  )}
                  {props.mode === 'edit' && props.type === 'seller' && (
                    <Button
                      htmlType='button'
                      danger
                      type='primary'
                      icon={<DeleteOutlined />}
                      onClick={showDeleteConfirm}
                    >
                      Удалить
                    </Button>
                  )}
                </div>
                {savePortalTarget && createPortal(
                  <Button
                    htmlType='button'
                    type='primary'
                    size='large'
                    icon={<SaveOutlined />}
                    loading={isSubmitting}
                    onClick={onSubmit}
                    disabled={isLegalINNChecking}
                  >
                    Сохранить
                  </Button>,
                  savePortalTarget
                )}
              </section>
            </>
          )}
        </Form>
      )}
      {props.mode === 'edit' && (
        <Modal
          open={isHiddenCategoriesOpen}
          centered
          width={520}
          className={styles.hiddenCategoriesModal}
          title={
            <div className={styles.hiddenCategoriesModalHead}>
              <Typography.Text className={styles.hiddenCategoriesModalTitle}>
                Категории продавца в выдаче
              </Typography.Text>
              <Typography.Text
                className={styles.hiddenCategoriesModalDescription}
              >
                Скрывайте подкатегории из витрины. Товары не удаляются.
              </Typography.Text>
            </div>
          }
          closeIcon={<CloseOutlined />}
          onCancel={() => setIsHiddenCategoriesOpen(false)}
          destroyOnClose
          footer={
            <div className={styles.hiddenCategoriesModalFooter}>
              <div className={styles.hiddenCategoriesModalFooterButtons}>
                <Button onClick={() => setIsHiddenCategoriesOpen(false)}>
                  Отменить
                </Button>
                <Button
                  type='primary'
                  loading={isHiddenCategoriesSaving}
                  onClick={saveHiddenCategoriesSettings}
                >
                  Сохранить
                </Button>
              </div>
              <Typography.Text className={styles.hiddenCategoriesModalHint}>
                Подсказка: «Скрыть товары» имеет приоритет над категориями.
              </Typography.Text>
            </div>
          }
        >
          <div className={styles.hiddenCategoriesModalBody}>
            <Input
              value={hiddenCategoriesSearch}
              onChange={(e) => setHiddenCategoriesSearch(e.target.value)}
              placeholder='Поиск'
              suffix={
                <SearchOutlined className={styles.hiddenCategoriesSearchIcon} />
              }
              allowClear
            />
            <div className={styles.hiddenCategoriesActions}>
              <Button
                className={classNames(styles.hiddenCategoriesFilterButton, {
                  [styles.hiddenCategoriesFilterButtonActive]:
                    hiddenCategoriesFilter === 'all'
                })}
                onClick={handleShowAllCategories}
              >
                Показать все
              </Button>
              <Button
                className={classNames(styles.hiddenCategoriesFilterButton, {
                  [styles.hiddenCategoriesFilterButtonActive]:
                    hiddenCategoriesFilter === 'hideAll'
                })}
                onClick={handleHideAllCategories}
              >
                Скрыть все
              </Button>
              <Button
                className={classNames(styles.hiddenCategoriesFilterButton, {
                  [styles.hiddenCategoriesFilterButtonActive]:
                    hiddenCategoriesFilter === 'hidden'
                })}
                onClick={() =>
                  setHiddenCategoriesFilter((prev) =>
                    prev === 'hidden' ? 'all' : 'hidden'
                  )
                }
              >
                Только скрытые
              </Button>
            </div>
            {isHiddenCategoriesLoading ? (
              <div className={styles.hiddenCategoriesLoading}>
                <Spin />
              </div>
            ) : filteredCategoryNodes.length ? (
              <div className={styles.hiddenCategoriesList}>
                {filteredCategoryNodes.map((category) => {
                  const isExpanded = expandedCategories.includes(category.id)
                  return (
                    <div key={category.id}>
                      <div className={styles.hiddenCategoryRow}>
                        <div className={styles.hiddenCategoryLeft}>
                          {category.children.length ? (
                            <Button
                              type='text'
                              size='small'
                              icon={
                                isExpanded ? (
                                  <DownOutlined />
                                ) : (
                                  <RightOutlined />
                                )
                              }
                              className={styles.hiddenCategoryExpand}
                              onClick={() =>
                                handleToggleExpandedCategory(category.id)
                              }
                            />
                          ) : (
                            <span className={styles.hiddenCategoryExpand} />
                          )}
                          <Typography.Text
                            className={styles.hiddenCategoryTitle}
                            ellipsis={{ tooltip: category.title }}
                          >
                            {category.title}
                          </Typography.Text>
                        </div>
                        <Switch
                          className={styles.hiddenCategoriesSwitch}
                          checked={!isCategoryHidden(category.id)}
                          onChange={(isVisible) =>
                            handleCategoryHiddenChange(category.id, !isVisible)
                          }
                        />
                      </div>
                      {isExpanded &&
                        category.children.map((subCategory) => (
                          <div
                            key={subCategory.id}
                            className={styles.hiddenSubCategoryRow}
                          >
                            <div className={styles.hiddenSubCategoryLeft}>
                              <span
                                className={styles.hiddenSubCategorySpacer}
                              />
                              <Typography.Text
                                className={styles.hiddenSubCategoryTitle}
                                ellipsis={{ tooltip: subCategory.title }}
                              >
                                {subCategory.title}
                              </Typography.Text>
                            </div>
                            <Switch
                              className={styles.hiddenCategoriesSwitch}
                              checked={!isSubCategoryHidden(subCategory.id)}
                              onChange={(isVisible) =>
                                handleSubCategoryHiddenChange(
                                  subCategory.id,
                                  !isVisible
                                )
                              }
                            />
                          </div>
                        ))}
                    </div>
                  )
                })}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description='Категории не найдены'
              />
            )}
          </div>
        </Modal>
      )}
      <Modal
        open={hiddenCommentDraft !== null}
        centered
        title={isProfile ? 'Скрыть все мои товары' : 'Скрыть товары продавца'}
        okText='Скрыть'
        cancelText='Отмена'
        okButtonProps={{ disabled: !hiddenCommentDraft?.trim() }}
        confirmLoading={isHiddenSaving}
        onOk={() => saveHidden(true, hiddenCommentDraft?.trim())}
        onCancel={() => setHiddenCommentDraft(null)}
        afterOpenChange={(open) => open && hiddenCommentRef.current?.focus()}
        destroyOnClose
      >
        <Typography.Paragraph>
          Кратко опишите причину. Товары скроются после сохранения.
        </Typography.Paragraph>
        <Input.TextArea
          ref={hiddenCommentRef}
          value={hiddenCommentDraft ?? ''}
          onChange={(e) => setHiddenCommentDraft(e.target.value)}
          maxLength={HIDDEN_COMMENT_MAX_LENGTH}
          showCount
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder='Например: продавец в отпуске до 1 ноября'
          className={styles.hiddenComment}
        />
      </Modal>
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        phone={currentPhone}
        onClose={() => setIsPhoneModalOpen(false)}
        onVerified={onPhoneVerified}
      />
      <EmailVerificationModal
        isOpen={isEmailModalOpen}
        email={currentEmail}
        onClose={() => setIsEmailModalOpen(false)}
        onVerified={onEmailVerified}
      />
    </>
  )
}
